import { AxiosResponse } from 'axios'
import i18next, { TOptions } from 'i18next'
import { formatPercentage } from './string'

export function translateValidationErrors(errors: Record<string, string | string[]>) {
  return Object.keys(errors).reduce((acc: Record<string, Array<string | string[] | null>>, key) => {
    const match = key.match(/^(.+)\[(\d+)\]$/)
    if (match) {
      const [_, field, index] = match

      if (!acc[field]) {
        acc[field] = [] as Array<string | string[] | null>
      }

      if (Array.isArray(errors[key])) {
        acc[field][parseInt(index)] = errors[key].map((error: string) =>
          i18next.t(error, { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
        )
      } else {
        acc[field][parseInt(index)] = [
          i18next.t(errors[key], { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
        ]
      }

      return acc
    }

    if (Array.isArray(errors[key])) {
      acc[key] = errors[key].map((error: string) => translateValidationError(key, error))
    } else if (typeof errors[key] === 'string') {
      acc[key] = [translateValidationError(key, errors[key])]
    }

    return acc
  }, {})
}

function translateValidationError(field: string, message: string) {
  const [error, ...params] = message.split(':')

  if (params.length > 0 && !isNaN(parseFloat(params[0]))) {
    const translatedField = i18next.t(field, { ns: 'common' })
    switch (error) {
      case 'mustBeGreaterThan':
        return mustBeGreaterThan(translatedField, parseFloat(params[0]))
      case 'mustBeGreaterThanOrEqualTo':
        return mustBeGreaterThanOrEqualTo(translatedField, parseFloat(params[0]))
      case 'mustBeLessThan':
        return mustBeLessThan(translatedField, parseFloat(params[0]))
      case 'mustBeLessThanOrEqualTo':
        return mustBeLessThanOrEqualTo(translatedField, parseFloat(params[0]))
      case 'relayOnThresholdMustBeLessThan':
        return relayOnThresholdMustBeLessThan(parseFloat(params[0]))
      case 'relayOffThresholdMustBeGreaterThan':
        return relayOffThresholdMustBeGreaterThan(parseFloat(params[0]))
    }
  }

  return i18next.t(error, { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
}

export function mustBeString(field: string) {
  return i18next.t('mustBeString', {
    ns: 'validation',
    field
  })
}

export function mustBeStringOrNull(field: string) {
  return i18next.t('mustBeStringOrNull', {
    ns: 'validation',
    field
  })
}

export function mustBeNumber(field: string) {
  return i18next.t('mustBeNumber', {
    ns: 'validation',
    field
  })
}

export function mustBeInteger(field: string) {
  return i18next.t('mustBeInteger', {
    ns: 'validation',
    field
  })
}

export function mustBeFloat(field: string) {
  return i18next.t('mustBeFloat', {
    ns: 'validation',
    field
  })
}

export function mustBeBoolean(field: string) {
  return i18next.t('mustBeBoolean', {
    ns: 'validation',
    field
  })
}

export function isRequired(field: string) {
  return i18next.t('isRequired', {
    ns: 'validation',
    field
  })
}

export function mustBeLessThanCharacters(field: string, max: number) {
  return i18next.t('mustBeLessThanCharacters', {
    ns: 'validation',
    field,
    max
  })
}

export function mustBeGreaterThan(field: string, min: number) {
  return i18next.t('mustBeGreaterThan', {
    ns: 'validation',
    field,
    min
  })
}

export function mustBeGreaterThanOrEqualTo(field: string, min: number) {
  return i18next.t('mustBeGreaterThanOrEqualTo', {
    ns: 'validation',
    field,
    min
  })
}

export function mustBeLessThan(field: string, max: number) {
  return i18next.t('mustBeLessThan', {
    ns: 'validation',
    field,
    max
  })
}

export function mustBeLessThanOrEqualTo(field: string, max: number) {
  return i18next.t('mustBeLessThanOrEqualTo', {
    ns: 'validation',
    field,
    max
  })
}

export function relayOnThresholdMustBeLessThan(value: number) {
  return i18next.t('relayOnThresholdMustBeLessThan', {
    ns: 'validation',
    value: formatPercentage(value)
  })
}

export function relayOffThresholdMustBeGreaterThan(value: number) {
  return i18next.t('relayOffThresholdMustBeGreaterThan', {
    ns: 'validation',
    value: formatPercentage(value)
  })
}

export function circularReference(field: string) {
  return i18next.t('circularReference', {
    ns: 'validation',
    field
  })
}

export function mustBePowerSource(field: string) {
  return i18next.t('mustBePowerSource', {
    ns: 'validation',
    field
  })
}
